import { useState, useCallback, useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import RequestKeyModal from "../modal/requestKey";
import {
  SUBSCRIPTION_TYPE,
  SubscriptionPageOutletContext,
} from "api/interfaces/subscription.interface";
import { SubscriptionHistoryStyled } from "../SubscriptionStyled";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useOutletContext } from "react-router-dom";
import * as mediaQuery from "components/MediaQuery";
import EuRequestHistory from "../modal/requestKey/list/euRequest/EuRequestHistory";
import CoTerm from "./CoTerm";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { FaInfoCircle } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { IoChevronForward, IoChevronBackOutline } from "react-icons/io5";
import HistoryKeyList from "./table/HistoryKeyList";
import HistoryDetailSummary from "./table/HistoryDetailSummary";
import BigButton from "components/atoms/buttons/BigButton";
import SubscriptionOrgInfo from "./SubscriptionOrgInfo";

export type TabType = SUBSCRIPTION_TYPE | "REQUEST";

const drawerVariants = {
  hidden: { x: "100%", opacity: 0.9 },
  show: {
    x: 0,
    opacity: 1,
    transition: {
      // 열림: 스프링으로 빨리 치고 들어오되 잔진동 적게
      type: "spring",
      stiffness: 900, // ↑ 더 빠르게 치고 들어옴
      damping: 40, // ↓ 출렁임 줄이면서 속도 유지
      mass: 0.6,
      restDelta: 0.6, // 미세 꼬리 빨리 종료
      restSpeed: 40,
    },
  },
  exit: {
    x: "100%",
    opacity: 0.95,
    transition: {
      // 닫힘: 트윈으로 짧고 부드럽게 미끄러지듯
      type: "tween",
      ease: [0.22, 0.61, 0.36, 1], // easeOutCubic 계열
      duration: 0.16,
    },
  },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 0.3, transition: { duration: 0.08 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

export default function SubscriptionKeyHistory(): JSX.Element {
  const isMobile = mediaQuery.useIsMobile();
  const isDesktop = mediaQuery.useIsDesktop();

  const [isModal, setIsModal] = useState<{ [key: string]: boolean }>({
    request: false,
    activate: false,
    sendEmail: false,
  });

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { selectedRowAccount, tab, setButtons, isCoTermOpen, setIsCoTermOpen } =
    useOutletContext<SubscriptionPageOutletContext>();

  const onModal = (type: "activate" | "request" | "sendEmail") => {
    setIsModal((mod) => {
      return {
        ...mod,
        [type]: !mod[type],
      };
    });
  };

  const onToggleCoTerm = useCallback(() => {
    if (isMobile) {
      setIsCoTermOpen((p) => !p);
      return;
    }
    if (window.innerWidth < 1400) {
      if (!isCoTermOpen) {
        setTimeout(() => {
          document
            .getElementById("co-term-info")
            ?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    }
    setIsCoTermOpen((p) => !p);
  }, [isMobile, isCoTermOpen]);

  useEffect(() => {
    if (setButtons && selectedAccount.accountLevel === LEVEL_TYPE.EU) {
      setButtons(
        <RBACWrapper
          requiredPermissions={[
            "manageBillingAndLicenses",
            "manageSubscription",
          ]}
        >
          <BigButton
            label="Request Subscription Key"
            onClickBtn={() => onModal("request")}
            className="request-button"
          />
        </RBACWrapper>
      );
    }
  }, [selectedAccount.accountLevel, setButtons]);

  return (
    <>
      {selectedAccount.accountLevel === LEVEL_TYPE.EU && (
        <SubscriptionHistoryStyled.CoTermToggle
          onClick={onToggleCoTerm}
          aria-expanded={isCoTermOpen}
          aria-controls="co-term-drawer"
          title="What is Co-Term?"
        >
          <span>
            <FaInfoCircle /> What is Co-Term?
          </span>
          <span className="icon">
            {isCoTermOpen ? <IoChevronForward /> : <IoChevronBackOutline />}
          </span>
        </SubscriptionHistoryStyled.CoTermToggle>
      )}
      <SubscriptionHistoryStyled.EuSubscriptionWrapper
        $level={selectedAccount.accountLevel}
      >
        {!(isCoTermOpen && isMobile) && (
          <SubscriptionHistoryStyled.TableWrapper>
            {tab === "REQUEST" ? (
              <EuRequestHistory />
            ) : (
              <SubscriptionHistoryStyled.Inner>
                {selectedAccount.accountLevel !== LEVEL_TYPE.EU && (
                  <SubscriptionOrgInfo
                    selectedRowAccount={selectedRowAccount}
                  />
                )}
                <HistoryDetailSummary
                  type={tab as SUBSCRIPTION_TYPE}
                  selectedRowAccount={selectedRowAccount}
                />
                <HistoryKeyList
                  type={tab as SUBSCRIPTION_TYPE}
                  selectedRowAccount={selectedRowAccount}
                />
              </SubscriptionHistoryStyled.Inner>
            )}
          </SubscriptionHistoryStyled.TableWrapper>
        )}
        {selectedAccount.accountLevel === LEVEL_TYPE.EU &&
          (isDesktop ? (
            <AnimatePresence initial={false} mode="wait">
              {isCoTermOpen && (
                <SubscriptionHistoryStyled.CoTermAside>
                  <motion.div
                    className="co-term-overlay"
                    onClick={() => setIsCoTermOpen(false)}
                    variants={overlayVariants}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                  />
                  <motion.aside
                    id="co-term-drawer"
                    role="complementary"
                    aria-label="Co-Term information"
                    key="co-term-drawer"
                    className="co-term-drawer"
                    variants={drawerVariants}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    style={{ willChange: "transform, opacity" }}
                  >
                    <div className="drawer-body">
                      <CoTerm />
                    </div>
                  </motion.aside>
                </SubscriptionHistoryStyled.CoTermAside>
              )}
            </AnimatePresence>
          ) : (
            isCoTermOpen && <CoTerm />
          ))}
      </SubscriptionHistoryStyled.EuSubscriptionWrapper>

      {isModal.request && (
        <RequestKeyModal onModal={() => onModal("request")} />
      )}
    </>
  );
}
